/**
 * T200 — Multi-Upload + processing-status API hooks.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError } from '../api';
import { uploadKeys } from './keys';
import { useActiveTenantId } from '../../hooks/useTenantContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

export interface UploadedExpense {
  fileName: string;
  expenseId: string;
  attachmentId: string;
}

export interface UploadResponse {
  items: UploadedExpense[];
}

export type ProcessingPhase =
  | 'queued'
  | 'erechnung_check'
  | 'ocr'
  | 'field_extract'
  | 'summencheck'
  | 'done'
  | 'failed';

export interface ProcessingStatus {
  phase: ProcessingPhase;
  errorMessage: string | null;
  startedAt?: string;
  finishedAt?: string | null;
}

export async function uploadExpenseFiles(files: File[]): Promise<UploadResponse> {
  const fd = new FormData();
  for (const f of files) fd.append('files', f);
  const res = await fetch(`${API_BASE}/expense-upload`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, res.statusText, data);
  return data as UploadResponse;
}

export function useUploadExpenseFiles() {
  return useMutation({
    mutationFn: (files: File[]) => uploadExpenseFiles(files),
  });
}

export function useProcessingStatus(expenseId: string | undefined, enabled = true) {
  // Spec 004 US3 (T018) FR-G3-POLLING-CADENCE — also pause polling
  // when the document is hidden (tab inactive) so we don't burn
  // requests on belege the user isn't looking at. Re-resumes
  // automatically on visibilitychange (TanStack Query re-evaluates
  // `enabled` on hook re-render driven by the state listener below).
  const documentHidden = useDocumentHidden();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: uploadKeys.processingStatus(tenantId, expenseId!),
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/expense-processing-status/${expenseId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as ProcessingStatus;
    },
    enabled: !!expenseId && enabled && !documentHidden && tenantId !== null,
    refetchInterval: (query) => {
      const phase = query.state.data?.phase;
      if (phase === 'done' || phase === 'failed') return false;
      if (documentHidden) return false;
      return 1500;
    },
    refetchIntervalInBackground: false,
  });
}

function useDocumentHidden(): boolean {
  const [hidden, setHidden] = useState(() =>
    typeof document !== 'undefined' ? document.hidden : false,
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handle = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', handle);
    return () => document.removeEventListener('visibilitychange', handle);
  }, []);
  return hidden;
}
